import { defineStore } from 'pinia'
import { ref, reactive } from 'vue'
import tokenService from '../utils/tokenService.js'
import { useAuthStore } from './auth.js'

const TTL = 5 * 60 * 1000 // 5 minutos

export const useTimesStore = defineStore('times', () => {
  // State - reactive Maps para que Vue 3 trackee cambios
  const cache = reactive(new Map())       // key = "YYYY-MM-DD", value = TimesRow[]
  const lastFetch = reactive(new Map())   // key = "YYYY-MM-DD", value = timestamp
  const loading = ref(false)
  // Contador, no solo true/false: si dos ensureRange se solapan (ej. al
  // navegar rápido entre día/semana, o el refresco post-guardado pisando un
  // fetch de montaje todavía en vuelo), el que termine primero no debe
  // apagar "loading" mientras el otro sigue esperando su respuesta - eso
  // hacía que el overlay desapareciera de golpe mostrando la grilla todavía
  // vacía, antes de que el segundo fetch la llenara.
  let loadingCount = 0
  function beginLoading() {
    loadingCount++
    loading.value = true
  }
  function endLoading() {
    loadingCount = Math.max(0, loadingCount - 1)
    loading.value = loadingCount > 0
  }

  // Promesas en vuelo para evitar fetches duplicados del mismo rango
  const pendingFetches = new Map()

  // --- Helpers ---

  function dateRange(desde, hasta) {
    const days = []
    const current = new Date(desde + 'T12:00:00') // noon to avoid DST issues
    const end = new Date(hasta + 'T12:00:00')
    while (current <= end) {
      days.push(current.toISOString().split('T')[0])
      current.setDate(current.getDate() + 1)
    }
    return days
  }

  function isDayStale(day) {
    const fetched = lastFetch.get(day)
    if (!fetched) return true
    return Date.now() - fetched > TTL
  }

  function isDayCached(day) {
    return cache.has(day) && !isDayStale(day)
  }

  // Agrupa días faltantes en sub-rangos contiguos para minimizar calls
  function findMissingRanges(days) {
    const missing = days.filter(d => !isDayCached(d))
    if (missing.length === 0) return []

    const ranges = []
    let start = missing[0]
    let prev = missing[0]

    for (let i = 1; i < missing.length; i++) {
      const prevDate = new Date(prev + 'T12:00:00')
      const currDate = new Date(missing[i] + 'T12:00:00')
      const diffDays = Math.round((currDate - prevDate) / (1000 * 60 * 60 * 24))

      if (diffDays > 1) {
        ranges.push({ desde: start, hasta: prev })
        start = missing[i]
      }
      prev = missing[i]
    }
    ranges.push({ desde: start, hasta: prev })
    return ranges
  }

  // --- Core fetch ---
  // Fetchea SIN param title para obtener tanto title=0 como title=1
  async function fetchRange(desde, hasta) {
    const authStore = useAuthStore()
    const key = `${desde}_${hasta}_${authStore.userId}`

    // Si ya hay un fetch en vuelo para este rango exacto, reusar la promesa
    if (pendingFetches.has(key)) {
      return pendingFetches.get(key)
    }

    const promise = (async () => {
      try {
        const result = await tokenService.get(
          'Times?desde=' + desde + '&hasta=' + hasta + '&usuario=' + authStore.userId
        )
        const data = result.data || result

        // Distribuir rows por día y cachear
        const byDay = {}
        const days = dateRange(desde, hasta)
        days.forEach(d => { byDay[d] = [] })

        if (Array.isArray(data)) {
          data.forEach(row => {
            const day = row.ts_Fecha ? row.ts_Fecha.split('T')[0] : null
            if (day && Object.prototype.hasOwnProperty.call(byDay, day)) {
              byDay[day].push(row)
            }
          })
        }

        const now = Date.now()
        Object.entries(byDay).forEach(([day, rows]) => {
          cache.set(day, rows)
          lastFetch.set(day, now)
        })

        return data
      } finally {
        pendingFetches.delete(key)
      }
    })()

    pendingFetches.set(key, promise)
    return promise
  }

  // --- Actions ---

  // Asegura que todo el rango esté cacheado. Fetchea solo los días faltantes.
  async function ensureRange(desde, hasta) {
    const days = dateRange(desde, hasta)
    const missingRanges = findMissingRanges(days)

    if (missingRanges.length === 0) return

    beginLoading()
    try {
      await Promise.all(
        missingRanges.map(r => fetchRange(r.desde, r.hasta))
      )
    } finally {
      endLoading()
    }
  }

  // Pre-carga un rango en background sin bloquear
  function prefetch(desde, hasta) {
    ensureRange(desde, hasta).catch(() => {})
  }

  // Retorna datos combinados de un rango, opcionalmente filtrado por title
  function getRangeData(desde, hasta, title) {
    const days = dateRange(desde, hasta)
    let result = []

    days.forEach(day => {
      const dayData = cache.get(day)
      if (dayData) {
        result = result.concat(dayData)
      }
    })

    if (title !== undefined && title !== null) {
      result = result.filter(row => row.title === title)
    }

    return result
  }

  // Shortcut para un solo día
  function getDayData(fecha, title) {
    return getRangeData(fecha, fecha, title)
  }

  // Invalida cache de un día específico (llamar tras POST/PUT/DELETE)
  function invalidateDay(fecha) {
    cache.delete(fecha)
    lastFetch.delete(fecha)
  }

  // Invalida un rango completo
  function invalidateRange(desde, hasta) {
    const days = dateRange(desde, hasta)
    days.forEach(day => {
      cache.delete(day)
      lastFetch.delete(day)
    })
  }

  // Limpia todo el cache
  function clearAll() {
    cache.clear()
    lastFetch.clear()
  }

  return {
    cache,
    lastFetch,
    loading,
    ensureRange,
    prefetch,
    getRangeData,
    getDayData,
    invalidateDay,
    invalidateRange,
    clearAll
  }
})
